import mitt from 'mitt'

// Vue2/Vue3 どちらでも差し替えやすいように、singleton emitter をexportする
export const emitter = mitt()

/**
 * global-var 8123–10875 付近の `emitGlobalVarPopupDialog` が発火するイベント名一覧（上から順に emitter.js 内で grep 可能）。
 * 購読: 各ダイアログは wrapMittPopupHandler + this.$emitter.on（$eventHub.$on は廃止）。
 * レガシー発火: 未移行の this.$eventHub.$emit('open-…-dialog', …) は installLegacyEventHubGlobalVarPopupForwardToMitt で mitt に転送される。
 */
const GLOBAL_VAR_POPUP_EVENT_TYPES = Object.freeze([
  'open-bizreach-dialog',
  'open-realtimetransferappeal-dialog',
  'open-medleylink-dialog',
  'open-realtimetransfercommon-dialog',
  'open-test1-realtimetransfercommon-dialog',
  'open-test2-realtimetransfercommon-dialog',
  'open-workgate-popup-dialog'
])

const GLOBAL_VAR_POPUP_EVENT_SET = new Set(GLOBAL_VAR_POPUP_EVENT_TYPES)

/**
 * emitGlobalVarPopupDialog が emit する配列ペイロードを、既存の open メソッドへ渡す mitt 用ラッパー。
 * 登録: this.$emitter.on(ev, wrapMittPopupHandler(this, 'openFoo'))
 * 解除: this.$emitter.off(ev, this._mittXXX) ※ on に渡したのと同一参照が必要
 */
export function wrapMittPopupHandler(vm, methodName) {
  return function globalVarPopupMittForward(argList) {
    const fn = vm[methodName]
    if (typeof fn !== 'function') return
    fn.apply(vm, Array.isArray(argList) ? argList : [])
  }
}

/**
 * mitt では第2引数が1つだけのため、可変長引数は配列で渡す。
 */
export function emitGlobalVarPopupDialog(type, ...args) {
  if (!GLOBAL_VAR_POPUP_EVENT_SET.has(type)) {
    throw new Error(`emitGlobalVarPopupDialog: unknown event "${type}"`)
  }
  emitter.emit(type, args)
}

let legacyHubPopupForwardInstalled = false

/**
 * まだ this.$eventHub.$emit('open-…-dialog', …) のままの呼び出しを mitt 購読側へ届ける（一方向: hub → mitt）。
 * emitGlobalVarPopupDialog は hub を経由しないため二重発火しない。
 */
function installLegacyEventHubGlobalVarPopupForwardToMitt(Vue) {
  if (legacyHubPopupForwardInstalled) {
    return
  }
  const hub = Vue.prototype.$eventHub
  if (!hub) {
    return
  }
  for (const ev of GLOBAL_VAR_POPUP_EVENT_TYPES) {
    hub.$on(ev, function legacyHubGlobalVarPopupForward(...args) {
      emitter.emit(ev, args)
    })
  }
  legacyHubPopupForwardInstalled = true
}

/** RT を未送信で閉じたときのチェーン用（旧 $eventHub 名と同一文字列） */
export const RT_DIALOG_CLOSED_WITHOUT_SUBMIT =
  'realtimetransfer-dialog-closed-without-submit'

/** RT を送客完了後に閉じたとき（「送信しました」→閉じる）。他の仕事紹介への継続 close では発火しない */
export const RT_DIALOG_CLOSED_AFTER_SUBMIT =
  'realtimetransfer-dialog-closed-after-submit'

/** output 離脱など: チェーンを進めずに RT ダイアログを閉じる（$eventHub） */
export const FORCE_HIDE_RT_DIALOGS = 'force-hide-rt-dialogs'

/** 送客同意後サブボタン等、件数上限なしで次 RT を出す */
export const RT_CHAIN_ADVANCE_REQUEST = 'rt-chain-advance-request'

/** RT 送客送信成功時の条件更新（FormatEdit / MyPage* / DownloadItem が購読） */
export const UPDATE_RT_CONDITION = 'update_rt_condition'

/** DownloadItem の自動 RT 起動 */
export const AUTO_RT = 'auto-rt'

/** RT ダイアログの戻る操作用カスタムイベント（ブラウザ標準 popstate とは別） */
export const RT_POPSTATE = 'popstate'

/** 汎用フルスクリーンローディングオーバーレイ（reason キーで多重待ち対応） */
export const LOADING_OVERLAY_SHOW = 'loading-overlay-show'
export const LOADING_OVERLAY_HIDE = 'loading-overlay-hide'

/** 共通 LoginDialog ライフサイクル（mitt 正本。旧 $eventHub 名と同一文字列） */
export const OPEN_LOGIN_DIALOG = 'open-login-dialog'
export const CLOSE_LOGIN_DIALOG = 'close-login-dialog'
export const LOGIN_DIALOG_CLOSED = 'login-dialog-closed'
export const LOGIN_SUCCESS = 'login-success'
export const SET_LOGIN_CONTEXT = 'set-login-context'

/**
 * Firebase Auth 成立通知（発火は当面 firebase-lib の hub。LoginDialog 等は mitt 購読可）。
 * hub→mitt forward のみ。mitt→hub 同期はしない。
 */
export const AUTH_STATE_CHANGED = 'auth_state_changed'

/** Firebase 初期化完了（ログイン/ログアウト双方で発火。旧 $eventHub 名と同一文字列） */
export const FIREBASE_INITIALIZED = 'firebase-initialized'

/** ログイン処理中のフルスクリーンローディング表示切り替え */
export const SET_FULLSCREEN_LOADING = 'set-fullscreen-loading'

/** 設定（AccountDialog）。mitt 正本。旧 $eventHub 名と同一文字列 */
export const OPEN_ACCOUNT_DIALOG = 'open-account-dialog'

/** 書式 AI mypr ダイアログを閉じる（一過性。開くのは親ローカル visible） */
export const HIDE_AI_MYPR_DIALOG = 'hide-ai-mypr-dialog'

const LOGIN_DIALOG_HUB_FORWARD_EVENT_TYPES = Object.freeze([
  OPEN_LOGIN_DIALOG,
  CLOSE_LOGIN_DIALOG,
  LOGIN_DIALOG_CLOSED,
  LOGIN_SUCCESS,
  SET_LOGIN_CONTEXT,
  AUTH_STATE_CHANGED,
  'open-loginform-dialog'
])

/**
 * LoginDialog を開く。任意の context は LoginDialog 側で set-login-context へ転送される。
 * @param {unknown} [context]
 */
export function emitOpenLoginDialog(context) {
  emitter.emit(OPEN_LOGIN_DIALOG, context)
}

export function emitOpenAccountDialog() {
  emitter.emit(OPEN_ACCOUNT_DIALOG)
}

export function emitHideAiMyprDialog() {
  emitter.emit(HIDE_AI_MYPR_DIALOG)
}

function emitCloseLoginDialog() {
  emitter.emit(CLOSE_LOGIN_DIALOG)
}

function emitLoginDialogClosed() {
  emitter.emit(LOGIN_DIALOG_CLOSED)
}

export function emitLoginSuccess() {
  emitter.emit(LOGIN_SUCCESS)
}

/** @param {unknown} context */
export function emitSetLoginContext(context) {
  emitter.emit(SET_LOGIN_CONTEXT, context)
}

/** 閉じ確定時: close + closed を 1 セット（購読ゼロの close も含め互換維持） */
export function emitLoginDialogClosePair() {
  emitCloseLoginDialog()
  emitLoginDialogClosed()
}

let legacyHubLoginDialogForwardInstalled = false

/**
 * 未移行の hub `$emit`（login 系）を mitt 購読側へ届ける（一方向: hub → mitt）。
 * emit* helper は hub を経由しないため二重発火しない。
 */
function installLegacyEventHubLoginDialogForwardToMitt(Vue) {
  if (legacyHubLoginDialogForwardInstalled) {
    return
  }
  const hub = Vue.prototype.$eventHub
  if (!hub) {
    return
  }
  for (const ev of LOGIN_DIALOG_HUB_FORWARD_EVENT_TYPES) {
    hub.$on(ev, function legacyHubLoginDialogForward(payload) {
      emitter.emit(ev, payload)
    })
  }
  legacyHubLoginDialogForwardInstalled = true
}

export function emitRtDialogClosedWithoutSubmit() {
  emitter.emit(RT_DIALOG_CLOSED_WITHOUT_SUBMIT)
}

export function emitRtDialogClosedAfterSubmit() {
  emitter.emit(RT_DIALOG_CLOSED_AFTER_SUBMIT)
}

/** @param {'request-other-job'|'sub-accept'} [trigger='request-other-job'] */
export function emitRtChainAdvanceRequest(trigger = 'request-other-job') {
  emitter.emit(RT_CHAIN_ADVANCE_REQUEST, trigger)
}

/** @param {string} reason */
export function emitLoadingOverlayShow(reason) {
  emitter.emit(LOADING_OVERLAY_SHOW, reason)
}

/** @param {string} reason */
export function emitLoadingOverlayHide(reason) {
  emitter.emit(LOADING_OVERLAY_HIDE, reason)
}

let eprintEventForwardInstalled = false

function installEprintEventForwards(Vue) {
  if (eprintEventForwardInstalled) return
  const hub = Vue.prototype.$eventHub
  if (!hub) return
  const forwarding = Object.create(null)
  function hubToMitt(ev) {
    hub.$on(ev, function eprintHubToMitt(payload) {
      if (forwarding[ev]) return
      forwarding[ev] = true
      try {
        emitter.emit(ev, payload)
      } finally {
        forwarding[ev] = false
      }
    })
  }
  function mittToHub(ev) {
    emitter.on(ev, function eprintMittToHub(payload) {
      if (forwarding[ev]) return
      forwarding[ev] = true
      try {
        hub.$emit(ev, payload)
      } finally {
        forwarding[ev] = false
      }
    })
  }
  // 双方向: mixin / FormatEdit(hub) ↔ useFormatLocalData(mitt)
  ;['format-data-changed', 'format-otherdata-changed'].forEach(function (ev) {
    hubToMitt(ev)
    mittToHub(ev)
  })
  // HistoryItem は mitt 発火。Skillsheet / WithImage は未移行 hub $on
  hubToMitt('deactivate-history-item')
  mittToHub('deactivate-history-item')
  // FormatEdit(hub) → HistoryItem(mitt) の textarea リサイズ
  hubToMitt('card-changed')
  hubToMitt('set_is_sendedYagishIntern')
  hubToMitt('open-ai-reason-dialog')
  // FormatEdit(hub) → EnglishResumePreview(mitt)。Preview は mitt 購読
  hubToMitt('show-preview-from-card')
  // EnglishResumePreview(mitt) → FormatEdit(hub)。Edit 側は未移行 hub $on
  mittToHub('go-card-to')
  emitter.on('display_rt_popup', function displayRtPopupMittToHub(payload) {
    hub.$emit('display_rt_popup', payload)
  })
  emitter.on('eprint-dialog-closed', function eprintClosedMittToHub(payload) {
    hub.$emit('eprint-dialog-closed', payload)
  })
  // DownloadItem は mitt 発火。FormatEdit / MyPage / MyPageRirekishoEdit は未移行 hub $on
  ;[
    'send-special-post',
    'send-special-quickemail',
    'mail-PDF',
    'download-PDF',
    'send-email-verification',
    'show-preview-4conv'
  ].forEach(function (ev) {
    emitter.on(ev, function downloadItemMittToHub(payload) {
      hub.$emit(ev, payload)
    })
  })
  eprintEventForwardInstalled = true
}

/** 課金 UI クラスタ移行中: 未移行 hub ↔ mitt の白リスト forward（named helper は増やさない） */
const YAGIPRIME_PAYMENT_HUB_TO_MITT = Object.freeze([
  'open-fontpayment-dialog',
  'open-yagiprimefontpayment-dialog',
  'show-yagiprime-payment-dialog',
  'open-yagiprime-payment-dialog',
  'payment_yagiprime_result',
  'show-private-relay-warning'
])

const YAGIPRIME_PAYMENT_MITT_TO_HUB = Object.freeze([
  'yagiprimefontpayment-dialog-closed',
  'yagiprime-payment-success',
  'payment_yagiprime_result',
  'show-private-relay-warning'
])

let yagiprimePaymentEventForwardInstalled = false

function installYagiprimePaymentEventForwards(Vue) {
  if (yagiprimePaymentEventForwardInstalled) return
  const hub = Vue.prototype.$eventHub
  if (!hub) return
  const forwarding = Object.create(null)
  for (const ev of YAGIPRIME_PAYMENT_HUB_TO_MITT) {
    hub.$on(ev, function yagiprimePaymentHubToMitt(payload) {
      if (forwarding[ev]) return
      forwarding[ev] = true
      try {
        emitter.emit(ev, payload)
      } finally {
        forwarding[ev] = false
      }
    })
  }
  for (const ev of YAGIPRIME_PAYMENT_MITT_TO_HUB) {
    emitter.on(ev, function yagiprimePaymentMittToHub(payload) {
      if (forwarding[ev]) return
      forwarding[ev] = true
      try {
        hub.$emit(ev, payload)
      } finally {
        forwarding[ev] = false
      }
    })
  }
  yagiprimePaymentEventForwardInstalled = true
}

/** メールリンク送付結果（authMethods は hub emit。LoginSpecialForms は mitt 購読） */
export const SIGN_IN_MAIL_SEND = 'sing-in-mail-send'
export const SIGN_IN_MAIL_FAIL = 'sing-in-mail-fail'

const SIGN_IN_MAIL_HUB_TO_MITT = Object.freeze([
  SIGN_IN_MAIL_SEND,
  SIGN_IN_MAIL_FAIL
])

let signInMailEventForwardInstalled = false

function installSignInMailEventForwards(Vue) {
  if (signInMailEventForwardInstalled) return
  const hub = Vue.prototype.$eventHub
  if (!hub) return
  for (const ev of SIGN_IN_MAIL_HUB_TO_MITT) {
    hub.$on(ev, function signInMailHubToMitt(payload) {
      emitter.emit(ev, payload)
    })
  }
  signInMailEventForwardInstalled = true
}

const EmitterPlugin = {
  install: function (Vue) {
    Vue.prototype.$emitter = emitter
    installLegacyEventHubGlobalVarPopupForwardToMitt(Vue)
    installLegacyEventHubLoginDialogForwardToMitt(Vue)
    installEprintEventForwards(Vue)
    installYagiprimePaymentEventForwards(Vue)
    installSignInMailEventForwards(Vue)
  }
}

export default EmitterPlugin
